/**
 * Calls to the search API's /admin endpoints.
 *
 * The API enforces both admin gates on every request — role = 'admin' in
 * profiles, and an aal2 session (authenticator code entered). The page checks
 * the same two things first only so it can show the right screen; it is not
 * the control.
 */
import { supabase } from "@/lib/supabase";

const SEARCH_API_URL = process.env.REACT_APP_SEARCH_API_URL || "";

export const adminApiConfigured = () => Boolean(SEARCH_API_URL);

/** API error codes worth a sentence of their own. */
const MESSAGES = {
  mfa_required: "Enter your authenticator code to continue.",
  not_admin: "This account is not an admin.",
  auth_unavailable: "Couldn't confirm admin access with Supabase. Try again in a moment.",
  not_authenticated: "You're signed out. Log in again.",
  invalid_token: "Your session expired. Log in again.",
};

async function adminFetch(path, { method = "GET", body } = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw Object.assign(new Error(MESSAGES.not_authenticated), { code: "not_authenticated" });

  const res = await fetch(`${SEARCH_API_URL}${path}`, {
    method,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    /* non-JSON error page, e.g. nginx */
  }

  if (!res.ok) {
    const code = typeof payload?.detail === "string" ? payload.detail : "";
    const message = MESSAGES[code] || code || `Request failed (${res.status})`;
    throw Object.assign(new Error(message), { code, status: res.status });
  }
  return payload;
}

export const adminSearch = (filters) => adminFetch("/admin/search", { method: "POST", body: filters });

export const adminQuery = (sql) => adminFetch("/admin/query", { method: "POST", body: { sql } });

export const adminStats = (deep = false) => adminFetch(`/admin/stats${deep ? "?deep=true" : ""}`);
